import React, { useEffect, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type PwaInstallPromptProps = {
  workspaceName: string;
};

const isRunningAsInstalledApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIosSafari = () => {
  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && !/CriOS|FxiOS|EdgiOS/.test(userAgent);
};

/** A browser-independent entry point for installing the PWA. */
export const PwaInstallPrompt = ({ workspaceName }: PwaInstallPromptProps) => {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || isRunningAsInstalledApp()) return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    const handleAppInstalled = () => {
      setInstallEvent(null);
      setShowIosHelp(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    if (isIosSafari()) setShowIosHelp(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    setInstallEvent(null);
  };

  const visible = !!installEvent || showIosHelp;
  if (!visible || !workspaceName) return null;

  return (
    <Modal transparent visible animationType="fade" onRequestClose={() => { setInstallEvent(null); setShowIosHelp(false); }}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Instale a app {workspaceName}</Text>
          {showIosHelp ? (
            <Text style={styles.description}>
              Toque no botão Partilhar do Safari e escolha “Adicionar ao ecrã principal”.
            </Text>
          ) : (
            <Text style={styles.description}>
              Instale a app no seu dispositivo para um acesso mais rápido.
            </Text>
          )}
          <View style={styles.actions}>
            <Pressable style={styles.dismissButton} onPress={() => { setInstallEvent(null); setShowIosHelp(false); }}>
              <Text style={styles.dismissText}>Agora não</Text>
            </Pressable>
            {!showIosHelp && (
              <Pressable style={styles.installButton} onPress={install}>
                <Text style={styles.installText}>Instalar</Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.45)', padding: 24 },
  card: { width: '100%', maxWidth: 380, borderRadius: 16, backgroundColor: '#fff', padding: 24, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 12, elevation: 8 },
  title: { color: '#242424', fontSize: 21, fontWeight: '700', marginBottom: 10 },
  description: { color: '#4a4a4a', fontSize: 16, lineHeight: 23 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 24 },
  dismissButton: { paddingHorizontal: 12, paddingVertical: 10 },
  dismissText: { color: '#6a6a6a', fontSize: 15, fontWeight: '600' },
  installButton: { borderRadius: 8, backgroundColor: '#990000', paddingHorizontal: 18, paddingVertical: 11 },
  installText: { color: '#fff', fontSize: 15, fontWeight: '700' },
});
